import { pick } from '@horde/engine';
import type { Action, MapDef, PlayerView, TerritoryId } from '@horde/engine';
import type { Bot } from './types';

function neighbors(map: MapDef, id: TerritoryId): readonly TerritoryId[] {
  return map.territories.find((t) => t.id === id)!.neighbors;
}

function defendersOf(view: PlayerView, id: TerritoryId): number {
  const t = view.territories[id]!;
  return t.owner === 'horde' || t.owner === null ? t.zombies : t.armies;
}

/** How exposed a territory is: enemy strength next door plus zombies inside. */
function threat(map: MapDef, view: PlayerView, id: TerritoryId): number {
  const me = view.viewer;
  const t = view.territories[id]!;
  let enemy = 0;
  for (const n of neighbors(map, id)) {
    const o = view.territories[n]!;
    if (o.owner !== me) enemy += defendersOf(view, n);
  }
  return enemy + t.zombies * 2 - t.armies;
}

/**
 * A simple but competent opponent:
 * - reinforces the most threatened border, and any pinned territory;
 * - plays hold cards where they matter most;
 * - purges when zombies threaten to pin, otherwise attacks favourable odds;
 * - occupies with everything; fortifies interior armies toward the front.
 */
export const greedyBot: Bot = {
  id: 'greedy',
  choose(map, view, legal, rng) {
    const me = view.viewer;
    const byType = <T extends Action['type']>(t: T) =>
      legal.filter((a): a is Extract<Action, { type: T }> => a.type === t);

    const occupy = byType('occupy');
    if (occupy.length) return [occupy[occupy.length - 1]!, rng];

    const cards = byType('playCard');
    for (const c of cards) {
      const t = view.territories[c.target!]!;
      if (c.cardId === 'cleanup-crew' && t.zombies >= 3) return [c, rng];
      if (c.cardId === 'airstrike' && t.owner !== me && t.zombies <= 3 && t.zombies > 0)
        return [c, rng];
      if (c.cardId === 'airstrike' && t.owner === me && t.zombies >= t.armies) return [c, rng];
      if (c.cardId === 'antidote' && t.zombies >= 2 && t.armies >= 2) return [c, rng];
    }

    const places = byType('place');
    if (places.length) {
      const best = places.reduce((a, b) =>
        threat(map, view, b.territoryId) > threat(map, view, a.territoryId) ? b : a,
      );
      return [{ ...best, count: view.reinforcementsPending }, rng];
    }

    const draw = byType('drawCard');
    if (draw.length) return [draw[0]!, rng];

    const attacks = byType('attack');
    if (attacks.length) {
      const purges = attacks.filter((a) => a.from === a.to);
      for (const p of purges) {
        const t = view.territories[p.from]!;
        if (t.zombies * 2 >= t.armies) return [p, rng];
      }
      const scored = attacks
        .filter((a) => a.from !== a.to)
        .map((a) => {
          const from = view.territories[a.from]!;
          const def = defendersOf(view, a.to);
          const odds = (from.armies - 1) / Math.max(1, def);
          const target = view.territories[a.to]!;
          const horde = target.owner === 'horde' || target.owner === null;
          // Zombies must be cleared eventually; accept thinner odds against them.
          const threshold = horde ? 1.0 : 1.4;
          return { a, score: odds - threshold + (horde ? 0.1 : 0), def };
        })
        .filter((s) => s.score >= 0 || s.def === 0)
        .sort((x, y) => y.score - x.score);
      if (scored.length) return [scored[0]!.a, rng];
      const end = legal.find((a) => a.type === 'endAttack');
      if (end) return [end, rng];
    }

    const forts = byType('fortify');
    if (forts.length) {
      const interior = forts.filter(
        (f) => threat(map, view, f.from) < 0 && threat(map, view, f.to) > threat(map, view, f.from),
      );
      if (interior.length) {
        const best = interior.reduce((a, b) =>
          threat(map, view, b.to) > threat(map, view, a.to) ? b : a,
        );
        return [best, rng];
      }
    }

    const end = legal.find((a) => a.type === 'endTurn');
    if (end) return [end, rng];
    return pick(rng, legal);
  },
};
